import type { TypedSupabaseClient, View } from '$lib/supabase'
import type { Database, Json } from '$lib/supabase/database.types'
import type { SupabaseClient } from '@supabase/supabase-js'

const rarity = ['common', 'rare', 'epic', 'legendary'] as const

type Rarity = (typeof rarity)[number]

export const achievementGroups = [
  'tester',
  'friends',
  'longest_streak',
  'completed_solo_sessions',
  'largest_group_session',
  'created_group_sessions',
  'joined_group_sessions',
  'completed_sessions',
  'streak_flames',
] as const

type AchievementGroup = (typeof achievementGroups)[number]
type AchievementCountGroup = Exclude<AchievementGroup, 'tester'>

export type Achievement = {
  id: string
  group: AchievementGroup
  rarity: Rarity
  name: string
  description: string
  achievedDate: string | undefined
  active: boolean
}

export type NumericalAchievement = Achievement & {
  currentNumber: number
  threshold: number
  isAccumulative: boolean
}

type CountCriteria = {
  not_accumulative: 'boolean'
  field: AchievementCountGroup
  threshold: number
}

export const isNumericalAchievement = (
  achievement: Achievement | NumericalAchievement,
): achievement is NumericalAchievement =>
  Object.hasOwn(achievement, 'currentNumber')

const getCurrentNumber = (
  profile: View<'profiles_with_stats'>,
  group: AchievementCountGroup,
) => {
  if (group === 'joined_group_sessions') {
    return (
      profile.completed_sessions -
      profile.created_group_sessions -
      profile.completed_solo_sessions
    )
  } else {
    return profile[group]
  }
}

export const getAchievements = async (
  supabase: TypedSupabaseClient,
  profile: View<'profiles_with_stats'>,
  {
    onlyAchieved = false,
    onlyNew = false,
  }: {
    onlyAchieved?: boolean
    supabase?: SupabaseClient<Database> | undefined
    onlyNew?: boolean
    lastNew?: string | null
  } = {},
): Promise<(Achievement | NumericalAchievement)[]> => {
  const query = supabase
    .from('achievements')
    .select(
      `*, owned:owned_achievements!owned_achievements_achievement_id_fkey${
        onlyAchieved ? '!inner' : ''
      }(*)`,
    )
    .eq('owned_achievements.user_uid', profile.id)
    .order('sort')

  if (onlyNew) {
    query.is('owned_achievements.seen_at', null)
  }

  const { data: dbAchievements, error } = await query

  if (error) {
    throw new Error(error.message)
  }

  const achievements: (Achievement | NumericalAchievement)[] = []

  for (const dbAchievement of dbAchievements) {
    const group = dbAchievement.id.replace(
      /_[\da-zA-Z]+$/,
      '',
    ) as AchievementGroup

    const criteria = dbAchievement.criteria as { [key: string]: Json }

    const countCriteria = Object.hasOwn(criteria, 'count')
      ? (criteria['count'] as CountCriteria)
      : undefined

    const ownedAchievement = dbAchievement.owned[0]

    if (dbAchievement.invisible && !ownedAchievement) {
      continue
    }

    achievements.push({
      id: dbAchievement.id,
      group,
      rarity: rarity[dbAchievement.rarity] ?? 'rare',
      name: dbAchievement.name,
      description: dbAchievement.description,
      active: false,
      achievedDate: ownedAchievement?.created_at,
      ...(countCriteria
        ? {
            currentNumber: getCurrentNumber(profile, countCriteria.field),
            threshold: countCriteria['threshold'],
            isAccumulative: !countCriteria['not_accumulative'],
          }
        : {}),
    })
  }

  let prevAchievement: Achievement | NumericalAchievement | undefined
  for (const achievement of achievements) {
    if (
      isNumericalAchievement(achievement) &&
      // If there is no previous achievement in this group
      (!prevAchievement ||
        prevAchievement.group !== achievement.group ||
        // Or if the previous achievement has already been achieved
        (prevAchievement.group === achievement.group &&
          prevAchievement.achievedDate))
    ) {
      achievement.active = true
    }

    prevAchievement = achievement
  }

  return achievements
}
